import { BLOCK_TAGS } from "../constants";
import type { ParsedNode, RawNode } from "../types";

const isElement = (node: Node | undefined): node is Element => !!node && node.nodeType === Node.ELEMENT_NODE;

const isText = (node: Node | undefined): node is Text => !!node && node.nodeType === Node.TEXT_NODE;

const isTag = (node: Node | undefined, tag: string): node is Element => isElement(node) && node.tagName === tag;

const isBr = (node: Node | undefined): node is Element => isTag(node, "BR");

const isBlock = (node: Node | undefined): node is Element => isElement(node) && BLOCK_TAGS.includes(node.tagName);

const isBlankText = (node: Node | undefined): boolean => isText(node) && !/\S/.test(node.data);

const isList = (node: Node): node is Element => isTag(node, "UL") || isTag(node, "OL");

const isItem = (node: Node): node is Element => isTag(node, "LI");

const escapeHtml = (text: string) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const inlineHtml = (nodes: Node[]) =>
  nodes.map((node) => (isText(node) ? escapeHtml(node.data) : isElement(node) ? node.outerHTML : "")).join("");

const topLevel = <T extends Element>(root: Element, selector: string): T[] =>
  Array.from(root.querySelectorAll<T>(selector)).filter((el) => !el.parentElement || !el.parentElement.closest("table, ul, ol"));

const raw = (html: string): RawNode => ({ type: "raw", level: 0, html });

const orRaw = <T extends Element>(el: T, parser: (el: T) => ParsedNode): ParsedNode => {
  try {
    return parser(el);
  } catch {
    return raw(el.outerHTML);
  }
};

export { isElement, isText, isTag, isBr, isBlock, isBlankText, isList, isItem, inlineHtml, topLevel, raw, orRaw };
